/*
 *  Copyright (C) GridGain Systems. All Rights Reserved.
 *  _________        _____ __________________        _____
 *  __  ____/___________(_)______  /__  ____/______ ____(_)_______
 *  _  / __  __  ___/__  / _  __  / _  / __  _  __ `/__  / __  __ \
 *  / /_/ /  _  /    _  /  / /_/ /  / /_/ /  / /_/ / _  /  _  / / /
 *  \____/   /_/     /_/   \_,__/   \____/   \__,_/  /_/   /_/ /_/
 */

import { Injectable } from '@angular/core';
import { DashboardApiService } from '@app/core/services/dashboard.service';
import { DashboardGroup } from '@shared/types/common';
import { uniq } from 'lodash-es';
import { Observable, of } from 'rxjs';
import { map, mapTo } from 'rxjs/operators';
import { MONITORING_DASHBOARD_GROUP_NAME } from '../constants/dashboard-id';
import { ReadonlyMonitoringDashboardsService } from './readonly-monitoring-dashboards.service';

@Injectable({ providedIn: 'root' })
export class MonitoringDashboardsService {
  constructor(
    private api: DashboardApiService,
    private readonlyWorkspaceDashboards: ReadonlyMonitoringDashboardsService,
  ) {}
  public createWorkspaceGroup(group: DashboardGroup): Observable<DashboardGroup> {
    return this.api.createGroup(group).pipe(mapTo(this.injectReadonlyDashboards(group)));
  }
  public getWorkspaceGroup(groupName: string) {
    return this.api.getGroup(groupName).pipe(map((group) => this.injectReadonlyDashboards(group)));
  }
  public injectReadonlyDashboards(group: DashboardGroup) {
    return group.name === MONITORING_DASHBOARD_GROUP_NAME
      ? {
          ...group,
          workspaceIds: uniq([
            ...this.readonlyWorkspaceDashboards.getAllDashboards().map(({ id }) => id),
            ...group.workspaceIds,
          ]),
        }
      : group;
  }
  public getWorkspaceDashboard(id: string) {
    const readonlyDashboard = this.readonlyWorkspaceDashboards.getDashboard(id);
    return readonlyDashboard ? of(readonlyDashboard) : this.api.get(id);
  }
  public updateDashboardGroup(group: DashboardGroup) {
    return this.api.updateGroup({
      ...group,
      workspaceIds: group.workspaceIds.filter((id) => !this.readonlyWorkspaceDashboards.isReadonlyDashboard(id)),
    });
  }
}
